import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Shield, FileSignature, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// OTP Signature translations.
// Native languages (per req. Fabrizio 08/06/2026): IT/UK/FR/ES/DE/PL.
// BE→FR, CH→DE, GBZ/IRL→UK (aliases). NL/PT/SE/NO/FI/DK/CZ/GR → UK fallback.
const OTP_TRANSLATIONS = {
  IT: { title: "Firma tramite Codice OTP", desc: "Riceverai un codice di verifica a 6 cifre", chooseChannel: "Come vuoi ricevere il codice?", channelEmail: "Email", channelSMS: "SMS", sendBtn: "Invia Codice OTP", sending: "Invio in corso...", verifyTitle: "Inserisci il Codice OTP", sentTo: "Codice inviato a", validFor: "Codice valido per", confirmBtn: "Conferma Firma", verifying: "Verifica in corso...", resend: "Non hai ricevuto il codice? Invia di nuovo", completeCode: "Inserisci il codice completo a 6 cifre", otpSent: "Codice OTP inviato", signed: "Contratto firmato con successo tramite OTP!", invalidOtp: "Codice OTP non valido", sendError: "Errore nell'invio del codice OTP", serverError: "Errore tecnico del server. Riprova tra qualche istante.", clausesRequired: "Devi approvare le clausole specifiche per procedere", clausesIntro: "Con la presente dichiaro di approvare espressamente le seguenti clausole delle Condizioni Generali di Contratto: art. 4.4 Esclusioni di responsabilità; art. 5.3 Facoltà di sospensione del contratto; art. 5.5 Limitazione di responsabilità di LoJack; artt. 6.1 e 7.3 Condizioni sospensive del Servizio; art. 12 Condizioni per la corretta erogazione del Servizio; art. 14.2 Legge applicabile (non applicabile qualora il Cliente sia un Consumatore ai sensi della normativa sui consumatori).", clausesNote: "" },
  UK: { title: "Sign via OTP Code", desc: "You will receive a 6-digit verification code", chooseChannel: "How do you want to receive the code?", channelEmail: "Email", channelSMS: "SMS", sendBtn: "Send OTP Code", sending: "Sending...", verifyTitle: "Enter OTP Code", sentTo: "Code sent to", validFor: "Code valid for", confirmBtn: "Confirm Signature", verifying: "Verifying...", resend: "Didn't receive the code? Resend", completeCode: "Enter the complete 6-digit code", otpSent: "OTP code sent", signed: "Contract signed successfully via OTP!", invalidOtp: "Invalid OTP code", sendError: "Error sending OTP code", clausesRequired: "You must approve the specific clauses to proceed", clausesIntro: "I hereby declare to approve the following clauses from General Terms and Conditions: art. 4.4 Responsibility Exclusions; art. 5.3 Faculty of contract suspension; art. 5.5 LoJack liability restriction; artt. 6.1 and 7.3 Suspensive conditions of Service; art. 12 Conditions for correct provision of Service; art. 14.2 Governing Law (not applicable if Customer is a Consumer under Consumer Law).", clausesNote: "" },
  FR: { title: "Signer via Code OTP", desc: "Vous recevrez un code de vérification à 6 chiffres", chooseChannel: "Comment voulez-vous recevoir le code?", channelEmail: "Email", channelSMS: "SMS", sendBtn: "Envoyer Code OTP", sending: "Envoi en cours...", verifyTitle: "Entrez le Code OTP", sentTo: "Code envoyé à", validFor: "Code valide pour", confirmBtn: "Confirmer Signature", verifying: "Vérification...", resend: "Code non reçu? Renvoyer", completeCode: "Entrez le code complet à 6 chiffres", otpSent: "Code OTP envoyé", signed: "Contrat signé avec succès via OTP!", invalidOtp: "Code OTP invalide", sendError: "Erreur lors de l'envoi du code OTP", serverError: "Erreur technique du serveur. Veuillez réessayer dans un instant.", clausesRequired: "Vous devez approuver les clauses spécifiques pour continuer", clausesIntro: "Je déclare par la présente approuver expressément les clauses suivantes des Conditions Générales du Contrat : art. 4.4 Exclusions de responsabilité ; art. 5.3 Faculté de suspension du contrat ; art. 5.5 Limitation de responsabilité de LoJack ; art. 6.1 et 7.3 Conditions suspensives du Service ; art. 12 Conditions pour la bonne fourniture du Service ; art. 14.2 Loi applicable (non applicable lorsque le Client est un Consommateur au sens de la législation sur la consommation).", clausesNote: "" },
  ES: { title: "Firmar vía Código OTP", desc: "Recibirás un código de verificación de 6 dígitos", chooseChannel: "¿Cómo quieres recibir el código?", channelEmail: "Email", channelSMS: "SMS", sendBtn: "Enviar Código OTP", sending: "Enviando...", verifyTitle: "Introduce el Código OTP", sentTo: "Código enviado a", validFor: "Código válido por", confirmBtn: "Confirmar Firma", verifying: "Verificando...", resend: "¿No has recibido el código? Reenviar", completeCode: "Introduce el código completo de 6 dígitos", otpSent: "Código OTP enviado", signed: "¡Contrato firmado con éxito vía OTP!", invalidOtp: "Código OTP inválido", sendError: "Error al enviar el código OTP", serverError: "Error técnico del servidor. Inténtalo de nuevo en unos instantes.", clausesRequired: "Debes aprobar las cláusulas específicas para continuar", clausesIntro: "Por la presente declaro aprobar expresamente las siguientes cláusulas de las Condiciones Generales del Contrato: art. 4.4 Exclusiones de responsabilidad; art. 5.3 Facultad de suspensión del contrato; art. 5.5 Limitación de responsabilidad de LoJack; arts. 6.1 y 7.3 Condiciones suspensivas del Servicio; art. 12 Condiciones para la correcta prestación del Servicio; art. 14.2 Ley aplicable (no aplicable cuando el Cliente sea un Consumidor en virtud de la normativa de consumo).", clausesNote: "" },
  DE: { title: "Per OTP-Code unterschreiben", desc: "Sie erhalten einen 6-stelligen Verifizierungscode", chooseChannel: "Wie wollen Sie den Code erhalten?", channelEmail: "E-Mail", channelSMS: "SMS", sendBtn: "OTP-Code senden", sending: "Wird gesendet...", verifyTitle: "OTP-Code eingeben", sentTo: "Code gesendet an", validFor: "Code gültig für", confirmBtn: "Unterschrift bestätigen", verifying: "Wird verifiziert...", resend: "Code nicht erhalten? Erneut senden", completeCode: "Geben Sie den vollständigen 6-stelligen Code ein", otpSent: "OTP-Code gesendet", signed: "Vertrag erfolgreich per OTP unterschrieben!", invalidOtp: "Ungültiger OTP-Code", sendError: "Fehler beim Senden des OTP-Codes", serverError: "Technischer Serverfehler. Bitte versuchen Sie es gleich erneut.", clausesRequired: "Sie müssen die spezifischen Klauseln genehmigen, um fortzufahren", clausesIntro: "Hiermit erkläre ich, die folgenden Klauseln der Allgemeinen Vertragsbedingungen ausdrücklich zu genehmigen: Art. 4.4 Haftungsausschlüsse; Art. 5.3 Recht zur Vertragsaussetzung; Art. 5.5 Haftungsbeschränkung von LoJack; Art. 6.1 und 7.3 Aufschiebende Bedingungen des Dienstes; Art. 12 Bedingungen für die korrekte Erbringung des Dienstes; Art. 14.2 Anwendbares Recht (nicht anwendbar, sofern der Kunde Verbraucher im Sinne des Verbraucherrechts ist).", clausesNote: "" },
  PL: { title: "Podpisz kodem OTP", desc: "Otrzymasz 6-cyfrowy kod weryfikacyjny", chooseChannel: "Jak chcesz otrzymać kod?", channelEmail: "Email", channelSMS: "SMS", sendBtn: "Wyślij kod OTP", sending: "Wysyłanie...", verifyTitle: "Wprowadź kod OTP", sentTo: "Kod wysłany do", validFor: "Kod ważny przez", confirmBtn: "Potwierdź podpis", verifying: "Weryfikacja...", resend: "Nie otrzymałeś kodu? Wyślij ponownie", completeCode: "Wprowadź pełny 6-cyfrowy kod", otpSent: "Kod OTP wysłany", signed: "Umowa pomyślnie podpisana przez OTP!", invalidOtp: "Nieprawidłowy kod OTP", sendError: "Błąd podczas wysyłania kodu OTP", serverError: "Błąd techniczny serwera. Spróbuj ponownie za chwilę.", clausesRequired: "Musisz zatwierdzić konkretne klauzule, aby kontynuować", clausesIntro: "Niniejszym oświadczam, że wyraźnie zatwierdzam następujące klauzule Ogólnych Warunków Umowy: art. 4.4 Wyłączenia odpowiedzialności; art. 5.3 Prawo do zawieszenia umowy; art. 5.5 Ograniczenie odpowiedzialności LoJack; art. 6.1 i 7.3 Warunki zawieszające Usługi; art. 12 Warunki prawidłowego świadczenia Usługi; art. 14.2 Prawo właściwe (nie ma zastosowania, jeżeli Klient jest Konsumentem w rozumieniu przepisów o ochronie konsumentów).", clausesNote: "" },
  PT: { title: "Assinar via Código OTP", desc: "Receberá um código de verificação de 6 dígitos", chooseChannel: "Como deseja receber o código?", channelEmail: "E-mail", channelSMS: "SMS", sendBtn: "Enviar código OTP", sending: "A enviar...", verifyTitle: "Introduza o código OTP", sentTo: "Código enviado para", validFor: "Código válido por", confirmBtn: "Confirmar assinatura", verifying: "A verificar...", resend: "Não recebeu o código? Reenviar", completeCode: "Introduza o código completo de 6 dígitos", otpSent: "Código OTP enviado", signed: "Contrato assinado com sucesso via OTP!", invalidOtp: "Código OTP inválido", sendError: "Erro ao enviar o código OTP", clausesRequired: "Tem de aprovar as cláusulas específicas para prosseguir", clausesIntro: "Pela presente declaro aprovar expressamente as seguintes cláusulas das Condições Gerais do Contrato: art. 4.4 Exclusões de responsabilidade; art. 5.3 Faculdade de suspensão do contrato; art. 5.5 Limitação de responsabilidade da LoJack; arts. 6.1 e 7.3 Condições suspensivas do Serviço; art. 12 Condições para a correta prestação do Serviço; art. 14.2 Lei aplicável (não aplicável caso o Cliente seja um Consumidor nos termos da legislação de defesa do consumidor).", clausesNote: "" },
  NL: { title: "Ondertekenen via OTP-code", desc: "U ontvangt een verificatiecode van 6 cijfers", chooseChannel: "Hoe wilt u de code ontvangen?", channelEmail: "E-mail", channelSMS: "SMS", sendBtn: "OTP-code verzenden", sending: "Verzenden...", verifyTitle: "Voer de OTP-code in", sentTo: "Code verzonden naar", validFor: "Code geldig voor", confirmBtn: "Handtekening bevestigen", verifying: "Verifiëren...", resend: "Code niet ontvangen? Opnieuw verzenden", completeCode: "Voer de volledige 6-cijferige code in", otpSent: "OTP-code verzonden", signed: "Contract succesvol ondertekend via OTP!", invalidOtp: "Ongeldige OTP-code", sendError: "Fout bij verzenden van OTP-code", serverError: "Technische serverfout. Probeer het zo weer opnieuw.", clausesRequired: "U moet de specifieke clausules goedkeuren om door te gaan", clausesIntro: "Hierbij verklaar ik uitdrukkelijk de volgende clausules van de Algemene Voorwaarden goed te keuren: art. 4.4 Uitsluiting van aansprakelijkheid; art. 5.3 Recht op contractopschorting; art. 5.5 Beperking aansprakelijkheid LoJack; art. 6.1 en 7.3 Opschortende voorwaarden van de Dienst; art. 12 Voorwaarden voor correcte uitvoering van de Dienst; art. 14.2 Toepasselijk recht (niet van toepassing als de Klant een Consument is volgens de consumentenwetgeving).", clausesNote: "" },
  SE: null,
  NO: null,
  FI: null,
  DK: null,
  CZ: null,
  GR: null,
  BE: null,
  CH: null,
  GBZ: null,
  IRL: null,
};

// Resolve aliases.
OTP_TRANSLATIONS.BE = OTP_TRANSLATIONS.FR;
OTP_TRANSLATIONS.CH = OTP_TRANSLATIONS.DE;
OTP_TRANSLATIONS.GBZ = OTP_TRANSLATIONS.UK;
OTP_TRANSLATIONS.IRL = OTP_TRANSLATIONS.UK;
['SE', 'NO', 'FI', 'DK', 'CZ', 'GR'].forEach(code => {
  OTP_TRANSLATIONS[code] = OTP_TRANSLATIONS.UK;
});

// Default to UK for countries not explicitly listed (truly unknown)
const getOtpText = (country) => OTP_TRANSLATIONS[country] || OTP_TRANSLATIONS['UK'];

export const OTPSignature = ({ customerId, customerEmail, customerPhone, onSuccess, setSigningContract, country, termsAcceptedAt, otpDeliveryChannel = 'both' }) => {
  const [otpStep, setOtpStep] = useState('request');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [maskedTarget, setMaskedTarget] = useState('');
  const [usedChannel, setUsedChannel] = useState('email');
  // Mara CR P3 17/06/2026: il cliente NON sceglie più il canale. Il
  // backend lo override tramite `country_config.otp_delivery_channel`.
  // Inviamo sempre "email" come default; il backend ignorerà la query
  // e userà la policy del paese. Lasciato come costante per chiarezza.
  const channel = 'email';
  const [countdown, setCountdown] = useState(0);
  const [clauses1341Acknowledged, setClauses1341Acknowledged] = useState(false);
  // Granular audit timestamp — captured the moment the customer
  // ticks the vexatious-clauses checkbox; forwarded to verify-otp.
  const [clauses1341AcknowledgedAt, setClauses1341AcknowledgedAt] = useState(null);
  // Visual cue (shake animation) triggered when the customer clicks
  // "Send OTP" without ticking the vexatious-clauses checkbox first
  // — req. Fabrizio 10/06/2026: instead of a silently-disabled
  // button, intercept the click, show a localized toast AND shake
  // the amber box so it's obvious WHERE the missing input is.
  const [shakeClauses, setShakeClauses] = useState(false);
  const inputRefs = useRef([]);
  // Ref to the vexatious-clauses label for smooth-scroll on validation
  // failure — guarantees the box is centered in the viewport even on
  // mobile where it may have scrolled off-screen during data entry.
  const clausesLabelRef = useRef(null);
  const t = getOtpText(country);

  // Error boundary del flusso firma (post-mortem bug NameError 07/07/2026):
  // i 5xx/errori di rete NON devono mai mostrare il fuorviante
  // "Codice OTP non valido" — quello è riservato ai 4xx di validazione.
  const errorToastMessage = (error, fallback) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (!error?.response || status >= 500) return t.serverError;
    return typeof detail === 'string' && detail ? detail : fallback;
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const requestOTP = async () => {
    // Guard: vexatious-clauses checkbox must be ticked before issuing
    // the OTP. We INTERCEPT the click here (rather than disabling the
    // button) so the customer always gets clear feedback — toast in
    // their UI language + shake animation on the amber box pointing
    // them to the missing input.
    if (!clauses1341Acknowledged) {
      toast.error(t.clausesRequired, { duration: 4000 });
      // Smooth-scroll the amber box into the viewport BEFORE the
      // shake animation kicks in — guarantees the customer sees
      // both the toast (top-right) AND the shaking box (centered)
      // even on mobile where the box may be far off-screen.
      try {
        clausesLabelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) { /* legacy browsers without scrollIntoView options */ }
      setShakeClauses(true);
      // Auto-clear the shake after the animation completes so the
      // user can re-trigger it by clicking again.
      setTimeout(() => setShakeClauses(false), 700);
      return;
    }
    try {
      setOtpSending(true);
      await axios.post(`${API}/customers/${customerId}/accept-terms`, { accepted: true });
      const res = await axios.post(`${API}/customers/${customerId}/request-otp`, null, { params: { channel } });
      const usedCh = res.data.channel || channel;
      setUsedChannel(usedCh);
      setMaskedTarget(res.data.phone || res.data.email || '');
      setOtpStep('verify');
      setCountdown(600);
      toast.success(t.otpSent);
      setTimeout(() => inputRefs.current[0]?.focus(), 300);
    } catch (error) {
      toast.error(errorToastMessage(error, t.sendError));
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otpCode];
    pasted.split('').forEach((char, i) => { newOtp[i] = char; });
    setOtpCode(newOtp);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const verifyOTP = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) {
      toast.error(t.completeCode);
      return;
    }
    if (!clauses1341Acknowledged) {
      toast.error(t.clausesRequired);
      return;
    }
    try {
      setOtpVerifying(true);
      setSigningContract(true);
      await axios.post(`${API}/customers/${customerId}/verify-otp`, {
        otp_code: code,
        clauses_1341_acknowledged: true,
        // Granular audit trail (req. Fabrizio 09/06/2026): persist
        // the exact UI click moments so signed_contracts can prove
        // a 4-event consent sequence (read → terms → vexatious →
        // OTP) — reinforces art. 1341 c.c. validity.
        terms_accepted_at: termsAcceptedAt || null,
        clauses_1341_acknowledged_at: clauses1341AcknowledgedAt || null,
      });
      toast.success(t.signed);
      onSuccess();
    } catch (error) {
      toast.error(errorToastMessage(error, t.invalidOtp));
    } finally {
      setOtpVerifying(false);
      setSigningContract(false);
    }
  };

  const formatCountdown = () => {
    const m = Math.floor(countdown / 60);
    const s = countdown % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  if (otpStep === 'request') {
    return (
      <div className="animate-fade-in" data-testid="otp-request-section">
        <div className="bg-gray-800/50 rounded-xl p-5 text-center">
          <Shield className="w-10 h-10 text-lojack-red mx-auto mb-3" />
          <h4 className="text-white font-semibold mb-2">{t.title}</h4>
          <p className="text-gray-400 text-sm mb-4">{t.desc}</p>

          {/* Channel info — Mara CR P3 17/06/2026: il cliente NON
              sceglie più il canale. Il backend lo decide tramite la
              Country Policy gestita da Admin (otp_delivery_channel:
              email | sms | both).
              Fabrizio CR 28/02/2026: ora la UI mostra SOLO il canale
              che la policy del paese ha effettivamente abilitato, no
              più "email + phone" hardcoded. Per "both" mostriamo
              entrambi; per "email" solo email; per "sms" solo phone.
              Se manca il dato (es. "sms" ma customer senza phone),
              fallback su quello disponibile + warning soft. */}
          <div className="mb-4">
            {(() => {
              const ch = (otpDeliveryChannel || 'both').toLowerCase();
              const showEmail = (ch === 'email' || ch === 'both') && !!customerEmail;
              const showPhone = (ch === 'sms' || ch === 'both') && !!customerPhone;
              const both = showEmail && showPhone;
              if (both) {
                return (
                  <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-3 text-left" data-testid="otp-channel-info">
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1.5">{t.sentTo}</p>
                    <div className="flex flex-col gap-1 text-sm text-gray-200">
                      <span className="flex items-center gap-2"><Mail size={14} className="text-gray-500" />{customerEmail}</span>
                      <span className="flex items-center gap-2"><Phone size={14} className="text-gray-500" />{customerPhone}</span>
                    </div>
                  </div>
                );
              }
              // Single-channel display — respects policy. Falls back to the
              // other channel only when the policy-mandated one is missing
              // (defensive: avoids a blank box if data is incomplete).
              const single = showEmail ? customerEmail
                            : showPhone ? customerPhone
                            : (customerEmail || customerPhone);
              const Icon = showPhone && !showEmail ? Phone : Mail;
              return (
                <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-3 text-left" data-testid="otp-channel-info-single">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1.5">{t.sentTo}</p>
                  <span className="flex items-center gap-2 text-sm text-gray-200">
                    <Icon size={14} className="text-gray-500" />{single}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Approvazione specifica clausole (art. 1341 c.c.) — req.
              Fabrizio 09/06/2026: spostata QUI sopra "Invia OTP" per
              imporre la doppia accettazione PRIMA dell'invio del
              codice. Bottone invio OTP rimane disabled finché questa
              checkbox non è attiva (l'altra è già garantita dalla
              ContractModal che monta OTPSignature solo se
              termsAccepted=true). */}
          <label
            ref={clausesLabelRef}
            className={`flex items-start gap-3 bg-lojack-clause-500/10 border border-lojack-clause-500/30 rounded-xl p-3 mb-4 cursor-pointer hover:bg-lojack-clause-500/15 transition-colors text-left ${shakeClauses ? 'animate-shake-bounce border-lojack-clause-400 bg-lojack-clause-500/25 shadow-[0_0_0_3px_rgba(110,12,12,0.55)]' : ''}`}
            data-testid="clauses-1341-label"
          >
            <input
              type="checkbox"
              checked={clauses1341Acknowledged}
              onChange={(e) => {
                const checked = e.target.checked;
                setClauses1341Acknowledged(checked);
                // Granular audit trail (req. Fabrizio 09/06/2026):
                // capture the exact UI moment the customer ticked
                // (or un-ticked) the vexatious-clauses checkbox.
                if (checked) {
                  setClauses1341AcknowledgedAt(new Date().toISOString());
                  setShakeClauses(false);   // clear shake instantly on tick
                } else {
                  setClauses1341AcknowledgedAt(null);
                }
              }}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-lojack-clause-500"
              data-testid="clauses-1341-checkbox"
            />
            <span className="text-xs text-white leading-relaxed" data-testid="clauses-intro-text">
              {t.clausesIntro}
              <span className="block mt-1 font-mono text-white">art. 4.4, 5.3, 5.5, 6.1, 7.3, 12, 14.2</span>
              {t.clausesNote && <span className="block mt-1 text-white/80 text-[10px]">{t.clausesNote}</span>}
            </span>
          </label>

          <button
            onClick={requestOTP}
            disabled={otpSending}
            className={`btn-lojack w-full flex items-center justify-center gap-2 ${!clauses1341Acknowledged ? 'opacity-60' : ''}`}
            data-testid="request-otp-btn"
            title={!clauses1341Acknowledged ? t.clausesRequired : undefined}
          >
            {otpSending ? (
              <><div className="spinner-sm"></div>{t.sending}</>
            ) : (
              <><Send size={18} />{t.sendBtn}</>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="otp-verify-section">
      <div className="bg-gray-800/50 rounded-xl p-5">
        <div className="text-center mb-5">
          <Shield className="w-10 h-10 text-lojack-blue-500 mx-auto mb-2" />
          <h4 className="text-white font-semibold mb-1">{t.verifyTitle}</h4>
          <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
            {usedChannel === 'sms' ? <Phone size={14} /> : <Mail size={14} />}
            {t.sentTo} <span className="text-white">{maskedTarget}</span>
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-4" onPaste={handleOtpPaste}>
          {otpCode.map((digit, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(i, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold bg-gray-900 border-2 border-gray-600 rounded-lg text-white focus:border-lojack-red focus:outline-none transition-colors"
              data-testid={`otp-input-${i}`}
            />
          ))}
        </div>

        {countdown > 0 && (
          <p className="text-center text-gray-500 text-sm mb-4">
            {t.validFor} <span className="text-lojack-red font-semibold">{formatCountdown()}</span>
          </p>
        )}

        <button
          onClick={verifyOTP}
          disabled={otpVerifying || otpCode.join('').length !== 6}
          className={`btn-lojack w-full flex items-center justify-center gap-2 ${otpCode.join('').length !== 6 ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid="verify-otp-btn"
        >
          {otpVerifying ? (
            <><div className="spinner-sm"></div>{t.verifying}</>
          ) : (
            <><FileSignature size={18} />{t.confirmBtn}</>
          )}
        </button>

        <button
          onClick={() => { setOtpStep('request'); setOtpCode(['', '', '', '', '', '']); }}
          className="w-full text-center text-gray-400 hover:text-white text-sm mt-3 py-2 transition-colors"
          disabled={otpVerifying}
          data-testid="resend-otp-btn"
        >
          {t.resend}
        </button>
      </div>
    </div>
  );
};
